import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function ROICalculator() {
  const [teamMembers, setTeamMembers] = useState(2);
  const [hoursPerWeek, setHoursPerWeek] = useState(10);
  const [hourlyRate, setHourlyRate] = useState(50);
  const [socialAccounts, setSocialAccounts] = useState(5);
  const [postsPerWeek, setPostsPerWeek] = useState(15);

  // Calculations
  const currentMonthlyCost = teamMembers * hoursPerWeek * hourlyRate * 4;
  const timeSavedPercentage = 65; // MandaPost saves 65% of time
  const timeSaved = (hoursPerWeek * timeSavedPercentage / 100) * 4; // hours per month
  const moneySaved = timeSaved * hourlyRate;
  const annualSavings = moneySaved * 12;

  // Engagement improvement (average 35% increase)
  const currentEngagement = postsPerWeek * 100; // assume 100 engagements per post
  const improvedEngagement = currentEngagement * 1.35;
  const additionalEngagement = improvedEngagement - currentEngagement;

  // ROI calculation
  const mandaPostCost = 79; // Professional plan
  const monthlySavings = moneySaved - mandaPostCost;
  const roi = ((monthlySavings / mandaPostCost) * 100).toFixed(0);
  const paybackDays = (mandaPostCost / (moneySaved / 30)).toFixed(0);

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#e4f4ff]/20 via-white to-[#b3d9ff]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <a
            href="/tools"
            className="inline-flex items-center gap-2 text-[#22577A] font-medium mb-6 hover:text-[#38a3a5] transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Tools
          </a>

          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-money-dollar-circle-line mr-2"></i>
            ROI Calculator
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-[#22577A] mb-6">
            Calculate Your
            <span className="block text-[#38a3a5]">MandaPost ROI</span>
          </h1>

          <p className="text-xl text-gray-700 mb-8">
            See how much time and money you'll save with intelligent social media automation
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left: Input Form */}
            <div>
              <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-white rounded-3xl p-8 shadow-xl border-2 border-[#b3d9ff]">
                <h2 className="text-2xl font-bold text-[#22577A] mb-6">Your Current Situation</h2>

                <div className="space-y-6">
                  {/* Team Members */}
                  <div>
                    <label className="block text-sm font-semibold text-[#22577A] mb-2">
                      Team Members Managing Social Media
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="20"
                      value={teamMembers}
                      onChange={(e) => setTeamMembers(parseInt(e.target.value))}
                      className="w-full h-2 bg-[#e4f4ff] rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-sm text-gray-600 mt-2">
                      <span>1</span>
                      <span className="font-bold text-[#22577A] text-lg">{teamMembers}</span>
                      <span>20</span>
                    </div>
                  </div>

                  {/* Hours Per Week */}
                  <div>
                    <label className="block text-sm font-semibold text-[#22577A] mb-2">
                      Hours Spent Per Week (per person)
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="40"
                      value={hoursPerWeek}
                      onChange={(e) => setHoursPerWeek(parseInt(e.target.value))}
                      className="w-full h-2 bg-[#e4f4ff] rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-sm text-gray-600 mt-2">
                      <span>1h</span>
                      <span className="font-bold text-[#22577A] text-lg">{hoursPerWeek}h</span>
                      <span>40h</span>
                    </div>
                  </div>

                  {/* Hourly Rate */}
                  <div>
                    <label className="block text-sm font-semibold text-[#22577A] mb-2">
                      Average Hourly Rate ($)
                    </label>
                    <input
                      type="range"
                      min="20"
                      max="200"
                      step="5"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(parseInt(e.target.value))}
                      className="w-full h-2 bg-[#e4f4ff] rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-sm text-gray-600 mt-2">
                      <span>$20</span>
                      <span className="font-bold text-[#22577A] text-lg">${hourlyRate}</span>
                      <span>$200</span>
                    </div>
                  </div>

                  {/* Social Accounts */}
                  <div>
                    <label className="block text-sm font-semibold text-[#22577A] mb-2">
                      Number of Social Media Accounts
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="50"
                      value={socialAccounts}
                      onChange={(e) => setSocialAccounts(parseInt(e.target.value))}
                      className="w-full h-2 bg-[#e4f4ff] rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-sm text-gray-600 mt-2">
                      <span>1</span>
                      <span className="font-bold text-[#22577A] text-lg">{socialAccounts}</span>
                      <span>50</span>
                    </div>
                  </div>

                  {/* Posts Per Week */}
                  <div>
                    <label className="block text-sm font-semibold text-[#22577A] mb-2">
                      Posts Per Week
                    </label>
                    <input
                      type="range"
                      min="3"
                      max="100"
                      value={postsPerWeek}
                      onChange={(e) => setPostsPerWeek(parseInt(e.target.value))}
                      className="w-full h-2 bg-[#e4f4ff] rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-sm text-gray-600 mt-2">
                      <span>3</span>
                      <span className="font-bold text-[#22577A] text-lg">{postsPerWeek}</span>
                      <span>100</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Results */}
            <div>
              <div className="bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-3xl p-8 shadow-2xl text-white sticky top-24">
                <h2 className="text-2xl font-bold mb-6">Your MandaPost ROI</h2>

                <div className="space-y-6">
                  {/* Key Metrics */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="text-center">
                      <p className="text-white/80 text-sm mb-2">Monthly Savings</p>
                      <p className="text-5xl font-bold">${monthlySavings.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <p className="text-white/80 text-xs mb-1">ROI</p>
                      <p className="text-3xl font-bold">{roi}%</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <p className="text-white/80 text-xs mb-1">Payback Period</p>
                      <p className="text-3xl font-bold">{paybackDays} days</p>
                    </div>
                  </div>

                  {/* Detailed Breakdown */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between pb-3 border-b border-white/20">
                      <span className="text-white/80">Current Monthly Cost</span>
                      <span className="font-semibold">${currentMonthlyCost.toLocaleString()}</span>
                    </div>

                    <div className="flex items-center justify-between pb-3 border-b border-white/20">
                      <span className="text-white/80">Time Saved Per Month</span>
                      <span className="font-semibold">{timeSaved.toFixed(0)} hours</span>
                    </div>

                    <div className="flex items-center justify-between pb-3 border-b border-white/20">
                      <span className="text-white/80">Money Saved Monthly</span>
                      <span className="font-semibold">${moneySaved.toLocaleString()}</span>
                    </div>

                    <div className="flex items-center justify-between pb-3 border-b border-white/20">
                      <span className="text-white/80">MandaPost Cost</span>
                      <span className="font-semibold">-${mandaPostCost}</span>
                    </div>

                    <div className="flex items-center justify-between pb-3 border-b border-white/20">
                      <span className="text-white/80">Engagement Boost</span>
                      <span className="font-semibold">+{additionalEngagement.toFixed(0)}/week</span>
                    </div>

                    <div className="bg-white/10 rounded-xl p-4 mt-4">
                      <div className="flex items-center justify-between">
                        <span className="font-bold">Annual Savings</span>
                        <span className="text-2xl font-bold">${annualSavings.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <a
                    href="/signup"
                    className="block w-full py-4 bg-white text-[#22577A] rounded-lg font-semibold text-center hover:shadow-xl hover:scale-105 transition-all mt-6"
                  >
                    <i className="ri-rocket-line mr-2"></i>
                    Start Saving Today - Free Trial
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#e4f4ff]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#22577A] mb-4">How MandaPost Saves You Money</h2>
            <p className="text-xl text-gray-600">Real savings, real results</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-[#e4f4ff] rounded-full flex items-center justify-center mb-4">
                <i className="ri-time-line text-3xl text-[#22577A]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-3">65% Time Savings</h3>
              <p className="text-gray-700">
                Automate content creation, scheduling, and posting across all platforms. Spend less time on manual tasks.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-[#e4f4ff] rounded-full flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-3xl text-[#22577A]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-3">35% More Engagement</h3>
              <p className="text-gray-700">
                AI-optimized posting times and content suggestions increase your engagement rates automatically.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-[#e4f4ff] rounded-full flex items-center justify-center mb-4">
                <i className="ri-team-line text-3xl text-[#22577A]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-3">Scale Without Hiring</h3>
              <p className="text-gray-700">
                Manage more accounts and create more content without adding headcount. One platform, unlimited potential.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-white rounded-3xl p-12 border-2 border-[#b3d9ff] text-center">
            <div className="flex items-center justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-yellow-400 text-2xl"></i>
              ))}
            </div>
            <p className="text-2xl text-gray-800 mb-6 italic">
              "We saved over $40,000 in the first year by switching to MandaPost. Our small team now manages 15 client accounts that would have required 3 full-time employees before."
            </p>
            <p className="font-semibold text-[#22577A]">Sarah Mitchell</p>
            <p className="text-gray-600">Marketing Director, Digital Growth Agency</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
